import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SettingsIcon, WalletIcon, PanelTop } from 'lucide-react';

export interface MenuItemType {
  key: string;
  label?: string;
  icon?: React.ReactNode;
  route: string;
  badge?: number;
}

interface verticalNavProps {
  open: boolean;
  menuItems: MenuItemType[];
  pathname: string;
  credits: number;
  onDockTop: () => void;
  onShowCreditsModal: () => void;
}

export const VerticalNavBar: React.FC<verticalNavProps> = ({
  open,
  menuItems,
  pathname,
  credits,
  onDockTop,
  onShowCreditsModal,
}) => {
  const { t } = useTranslation('common');

  if (!open) return null;

  return (
    <aside className="gradient-surface left-12 top-32 z-50 hidden h-[70vh] w-14 flex-col items-center justify-between rounded-full px-1.5 py-3 md:absolute md:flex">
      <div className="flex flex-col items-start">
        {menuItems
          .filter(item => item.key === 'home')
          .map(item => {
            const isActive = pathname === item.route || pathname === '/';
            return (
              <Link
                key={item.key}
                to={item.route}
                className={`relative flex h-10 w-10 items-center justify-center rounded-full transition-all ${
                  isActive ? 'font-bold text-primary' : 'text-secondary/50 hover:text-secondary'
                }`}
                aria-label={item.label ?? item.key}
                title={item.label}
              >
                <span className="flex h-5 w-5 items-center justify-center">{item.icon}</span>
              </Link>
            );
          })}

        <div className="mt-2 flex flex-col items-center gap-3">
          {menuItems
            .filter(item => item.key !== 'home')
            .map(item => {
              const isActive = pathname === item.route;
              return (
                <Link
                  key={item.key}
                  to={item.route}
                  className={`relative flex h-10 w-10 items-center justify-center rounded-full transition-all ${
                    isActive ? 'font-bold text-primary' : 'text-secondary/50 hover:text-secondary'
                  }`}
                  aria-label={item.label ?? item.key}
                  title={item.label}
                >
                  <span className="flex h-5 w-5 items-center justify-center">{item.icon}</span>
                  {item.badge !== undefined && item.badge > 0 && (
                    <span className="absolute -right-0.5 -top-0.5 flex h-3 w-3 items-center justify-center rounded-full bg-warning text-[8px] text-lightest">
                      {item.badge}
                    </span>
                  )}
                </Link>
              );
            })}
        </div>
      </div>

      <div className="flex flex-col items-center gap-1">
        <Link
          to="/settings"
          className={`flex h-10 w-10 items-center justify-center rounded-full transition-all ${
            pathname === '/settings' ? 'text-primary' : 'text-secondary/50 hover:text-secondary'
          }`}
          aria-label={t('navBar.settings')}
          title={t('navBar.settings')}
        >
          <SettingsIcon className="h-5 w-5" />
        </Link>

        <button
          className="flex h-10 w-10 flex-col items-center justify-center text-secondary/60 transition-all hover:text-secondary"
          title={t('navBar.credits')}
          onClick={onShowCreditsModal}
        >
          <WalletIcon className="h-5 w-5" />
          <span className="text-[10px] font-semibold">{credits}</span>
        </button>

        <button
          type="button"
          onClick={onDockTop}
          className="flex h-10 w-10 items-center justify-center rounded-full text-secondary/50 transition-all hover:text-secondary"
          aria-label="Dock top"
          title={t('navBar.dockTop')}
        >
          <PanelTop className="h-5 w-5" />
        </button>
      </div>
    </aside>
  );
};
